import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import AsyncDropdown, { PropsWithAsyncDropdown } from 'core/elements/dropdown/AsyncDropdown'
import { DropdownItemSpec } from 'core/elements/dropdown/DropdownCommonProps'
import Tooltip, { TooltipProps } from 'core/elements/tooltip/Tooltip'
import useListAction from 'core/hooks/useListAction'
import { isRestrictedSsu } from 'core/utils/rbac'
import { listClusterHostGroups } from './actions'
import { clusterHostGroupsSelector } from './selectors'

interface Props {
  label?: string
  width?: number
  fullWidth?: boolean
  tooltip?: string
  tooltipProps?: TooltipProps
  includeNone?: boolean
  disableClusterWithoutHosts?: boolean
  autoSelectFirstValue?: boolean
  filterFn?: (item: any) => boolean
  optionMapFn?: (value: any) => DropdownItemSpec<string>
}

export default function ClusterHostGroupsPicklist({
  width = 180,
  fullWidth = false,
  label,
  tooltip = '',
  tooltipProps,
  includeNone = false,
  disableClusterWithoutHosts = false, // Mutually exclusive
  autoSelectFirstValue = false,
  filterFn,
  optionMapFn, // Mutually exclusive
  value,
  onChange,
  ...rest
}: PropsWithAsyncDropdown<Props>) {
  const { loading } = useListAction(listClusterHostGroups)
  const clusterHostGroups = useSelector(clusterHostGroupsSelector)

  const items = useMemo(() => {
    // For restricted SSUs, filter out any cluster starting with 'internal' string
    const accessibleHostGroups = isRestrictedSsu()
      ? clusterHostGroups.filter((cluster) => !cluster?.name?.startsWith('internal'))
      : clusterHostGroups
    const filteredHostGroups = filterFn
      ? accessibleHostGroups.filter(filterFn)
      : accessibleHostGroups
    const mapFn = optionMapFn
      ? optionMapFn
      : (clusterHostGroup) => {
          const disabled = disableClusterWithoutHosts && clusterHostGroup.hosts?.length === 0
          return {
            label: `${clusterHostGroup.name} ${disabled ? ' (No Hosts Assigned)' : ''}`,
            value: clusterHostGroup.name,
            disabled,
          }
        }
    const options = filteredHostGroups.map(mapFn).sort((a, b) => {
      // Sort disabled items to the end
      if (a.disabled && !b.disabled) return 1
      if (!a.disabled && b.disabled) return -1
      return 0
    })
    if (!includeNone) {
      return options
    }

    return [
      {
        label: '-- None --',
        value: '',
      },
      ...options,
    ]
  }, [clusterHostGroups, includeNone, disableClusterWithoutHosts, filterFn, optionMapFn])

  // Auto select first value when enabled
  useEffect(() => {
    if (!autoSelectFirstValue) return

    if (!loading && items.length > 0 && !value && onChange) {
      const firstEnabled = items.find((item) => !item.disabled)
      if (firstEnabled) {
        onChange(firstEnabled.value)
      }
    }
  }, [items, loading, autoSelectFirstValue, value, onChange])

  return (
    <Tooltip message={tooltip} {...tooltipProps}>
      <AsyncDropdown
        name={label}
        label={label}
        items={items}
        formField
        loading={loading}
        width={fullWidth ? undefined : width}
        value={value}
        onChange={onChange}
        {...rest}
      />
    </Tooltip>
  )
}
